import React from 'react';
import { ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const TYPE_META = {
  typsa_gg: { label: 'TYPSA', badge: 'bg-red-600 text-white', dot: 'bg-red-600', icon: 'text-red-600' },
  typsa_colab: { label: 'Colaboración TYPSA', badge: 'bg-sky-600 text-white', dot: 'bg-sky-600', icon: 'text-sky-600' },
  privada: { label: 'Privada', badge: 'bg-purple-600 text-white', dot: 'bg-purple-600' },
  publica: { label: 'Pública', badge: 'bg-green-600 text-white', dot: 'bg-green-600' },
  otros: { label: 'Sin tipo', badge: 'bg-secondary text-secondary-foreground', dot: 'bg-muted-foreground' },
};
const TYPE_ORDER = ['typsa_gg', 'typsa_colab', 'privada', 'publica', 'otros'];

const groupType = (g) => g.typsaKind || (TYPE_META[g.companyType] ? g.companyType : 'otros');

const TypsaKindTag = ({ kind }) => {
  if (!kind) return null;
  const isColab = kind === 'typsa_colab';
  return (
    <span
      data-testid={`typsa-kind-tag-${kind}`}
      className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded flex-shrink-0 ${isColab ? 'bg-sky-100 text-sky-700' : 'bg-red-100 text-red-700'}`}
    >
      {isColab ? 'Colab. TYPSA' : 'TYPSA'}
    </span>
  );
};

const CompanyGroup = ({ group, groupKey, expanded, onToggle, renderCard, mobile, hideTag }) => {
  const meta = TYPE_META[groupType(group)];
  return (
    <div className="space-y-2">
      <div
        data-testid={`kanban-group-${groupKey}`}
        className={mobile
          ? 'flex items-center justify-between p-3 bg-card rounded-lg cursor-pointer active:bg-muted/50 border'
          : 'flex items-center justify-between p-2 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors'}
        onClick={() => onToggle(groupKey)}
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown className="w-4 h-4 flex-shrink-0 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 flex-shrink-0 text-muted-foreground" />}
          <Building2 className={`w-3 h-3 flex-shrink-0 ${meta.icon || 'text-muted-foreground'}`} />
          <span className="text-sm font-medium truncate">{group.companyName}</span>
          {!hideTag && <TypsaKindTag kind={group.typsaKind} />}
        </div>
        <Badge className={`text-xs flex-shrink-0 ${meta.badge}`}>{group.opportunities.length}</Badge>
      </div>
      {expanded && <div className="space-y-2 pl-2">{group.opportunities.map(renderCard)}</div>}
    </div>
  );
};

export const KanbanGroups = ({ groups, keyPrefix, groupBy, expandedGroups, onToggle, renderCard, mobile = false }) => {
  const companyKey = (g) => `${keyPrefix}-${g.key || g.companyName}`;
  const renderCompany = (g, hideTag) => (
    <CompanyGroup
      key={companyKey(g)} group={g} groupKey={companyKey(g)} mobile={mobile} hideTag={hideTag}
      expanded={expandedGroups[companyKey(g)] ?? false} onToggle={(k) => onToggle(k, false)} renderCard={renderCard}
    />
  );

  if (groupBy !== 'client_type') return <>{groups.map((g) => renderCompany(g, false))}</>;

  return (
    <>
      {TYPE_ORDER.map((type) => {
        const inType = groups.filter((g) => groupType(g) === type);
        if (inType.length === 0) return null;
        const meta = TYPE_META[type];
        const typeKey = `${keyPrefix}-type-${type}`;
        const expanded = expandedGroups[typeKey] ?? true;
        const total = inType.reduce((s, g) => s + g.opportunities.length, 0);
        return (
          <div key={typeKey} className="space-y-2" data-testid={`kanban-type-section-${keyPrefix}-${type}`}>
            <div
              data-testid={`kanban-type-header-${keyPrefix}-${type}`}
              className="flex items-center justify-between px-2 py-1.5 rounded-md cursor-pointer border bg-card hover:bg-muted/40 transition-colors"
              onClick={() => onToggle(typeKey, true)}
            >
              <div className="flex items-center gap-2 min-w-0">
                {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
                <span className="text-xs font-semibold uppercase tracking-wide truncate">{meta.label}</span>
              </div>
              <Badge className={`text-xs flex-shrink-0 ${meta.badge}`}>{total}</Badge>
            </div>
            {expanded && <div className="pl-2 space-y-2">{inType.map((g) => renderCompany(g, true))}</div>}
          </div>
        );
      })}
    </>
  );
};
